import { useEffect, useMemo, useState } from "react";
import { listYggCycles, createYggCycle, getYggCycle, getYggEvaluation, submitYggCalibration, submitYggDirectorReview, fetchYggMetadata, fetchEmployees } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, ArrowRight, Sparkles, CheckCircle2, Users, User, ShieldCheck, Award, ClipboardList, Search, TrendingUp, Heart, FileBarChart } from "lucide-react";
import YggReportDialog from "./YggReportDialog";

const STAGE_ORDER = ["survey", "manager_eval", "director_review", "calibration"];
const STAGE_LABEL = { survey: "Anket", manager_eval: "Yönetici", director_review: "Direktör", calibration: "Kalibrasyon" };
const CODE_LABELS = {
  terfi_edebilir: "Terfi Edebilir", mevcut_rolde_devam: "Mevcut Rolde Devam",
  alaninin_uzmani: "Alanının Uzmanı", anahtar_calisan: "Anahtar Çalışan", tespit_icin_erken: "Tespit İçin Erken",
};

const nextStep = (p) => {
  const s = p?.stage_status || {};
  if (s.calibration === "completed") return { key: "done", label: "Tamamlandı", cls: "bg-emerald-100 text-emerald-800" };
  if (s.director_review === "completed") return { key: "calibration", label: "Sırada: İK Kalibrasyon", cls: "bg-amber-100 text-amber-800" };
  if (s.manager_eval === "completed") return { key: "director_review", label: "Sırada: Direktör", cls: "bg-indigo-100 text-indigo-800" };
  if (s.survey === "completed") return { key: "manager_eval", label: "Sırada: Yönetici", cls: "bg-sky-100 text-sky-800" };
  return { key: "survey", label: "Sırada: Çalışan Anketi", cls: "bg-slate-100 text-slate-600" };
};

export default function YggCyclesAdmin() {
  const [cycles, setCycles] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [detail, setDetail] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const reload = () => listYggCycles().then((d) => setCycles(d.items || []));
  const reloadDetail = () => { if (selectedCycle) getYggCycle(selectedCycle).then(setDetail); };
  useEffect(() => { reload(); fetchYggMetadata().then(setMetadata); }, []);
  useEffect(() => { if (selectedCycle) getYggCycle(selectedCycle).then(setDetail); else setDetail(null); }, [selectedCycle]);

  return (
    <div className="space-y-5" data-testid="ygg-cycles-admin">
      <ProcessExplainer />
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Yetenek Gözden Geçirme</div>
          <h3 className="font-display text-xl font-semibold">YGG Döngüleri</h3>
          <p className="text-xs text-slate-500 mt-0.5">Anket → Yönetici → Direktör → Kalibrasyon akışını buradan başlat ve takip et.</p>
        </div>
        <Button onClick={() => setOpenNew(true)} data-testid="ygg-cycle-new"><Plus className="w-3.5 h-3.5 mr-1" /> Yeni YGG Başlat</Button>
      </div>

      {cycles.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-slate-500">Henüz aktif YGG döngüsü yok. Yıllık gözden geçirme akışını başlatmak için &ldquo;Yeni YGG Başlat&rdquo; tıkla.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cycles.map((c) => (
            <CycleCard key={c.id} cycle={c} onOpen={() => setSelectedCycle(c.id)} />
          ))}
        </div>
      )}

      {detail && (
        <CycleDetail cycle={detail} metadata={metadata} onRefresh={reloadDetail} onClose={() => { setSelectedCycle(null); reload(); }} />
      )}

      <NewCycleDialog open={openNew} onOpenChange={setOpenNew} onSaved={reload} />
    </div>
  );
}

function CycleCard({ cycle, onOpen }) {
  const total = cycle.total_participants || 0;
  const progress = cycle.progress || {};
  const cur = cycle.current_stage || "survey";
  return (
    <div className="card-surface p-4 cursor-pointer hover:shadow-md transition" onClick={onOpen} data-testid={`ygg-cycle-card-${cycle.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-lg font-semibold">{cycle.name}</div>
          <div className="text-xs text-slate-500">{total} katılımcı · {cycle.status}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400" />
      </div>
      <div className="mt-3">
        <div className="text-[11px] text-slate-500 mb-1.5">Aşama ilerlemesi</div>
        <div className="grid grid-cols-4 gap-1.5">
          {STAGE_ORDER.map((s) => {
            const n = progress[s] || 0;
            const pct = total ? Math.round((n / total) * 100) : 0;
            const active = cur === s;
            return (
              <div key={s} className={`p-2 rounded-lg border ${active ? "border-sky-400 bg-sky-50" : "border-slate-100 bg-slate-50"}`}>
                <div className="text-[10px] font-semibold text-slate-600 uppercase truncate">{STAGE_LABEL[s]}</div>
                <div className="text-sm font-bold">{n}/{total}</div>
                <div className="text-[10px] text-slate-500">%{pct}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const FILTERS = [
  { key: "all", label: "Tümü" },
  { key: "survey", label: "Anket Bekleyen" },
  { key: "manager_eval", label: "Yönetici Bekleyen" },
  { key: "director_review", label: "Direktör Bekleyen" },
  { key: "calibration", label: "Kalibrasyon Bekleyen" },
  { key: "done", label: "Tamamlanan" },
];

function CycleDetail({ cycle, metadata, onRefresh, onClose }) {
  const parts = cycle.participants || [];
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [reportOpen, setReportOpen] = useState(false);

  const counts = useMemo(() => {
    const c = { all: parts.length, survey: 0, manager_eval: 0, director_review: 0, calibration: 0, done: 0 };
    parts.forEach((p) => { c[nextStep(p).key] += 1; });
    return c;
  }, [parts]);

  const filtered = useMemo(() => {
    let list = parts;
    if (filter !== "all") list = list.filter((p) => nextStep(p).key === filter);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((p) => (p.employee_name || "").toLowerCase().includes(q) || (p.department || "").toLowerCase().includes(q) || (p.employee_role || "").toLowerCase().includes(q));
    return list;
  }, [parts, filter, query]);

  return (
    <div className="card-surface p-5" data-testid="ygg-cycle-detail">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-eyebrow">Döngü Detayı</div>
          <h4 className="font-display text-lg font-semibold">{cycle.name}</h4>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setReportOpen(true)} data-testid="ygg-report-open">
            <FileBarChart className="w-3.5 h-3.5 mr-1" /> Sonuç Raporu
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>Kapat</Button>
        </div>
      </div>

      <YggReportDialog cycleId={cycle.id} open={reportOpen} onOpenChange={setReportOpen} />

      {/* KPI strip */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-testid="ygg-detail-kpis">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`p-2 rounded-lg border text-left transition ${filter === f.key ? "border-sky-400 bg-sky-50 ring-1 ring-sky-200" : "border-slate-100 bg-slate-50 hover:border-slate-200"}`}
            data-testid={`ygg-filter-${f.key}`}>
            <div className="text-lg font-bold text-slate-800">{counts[f.key]}</div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{f.label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="İsim, departman veya rol ara…" className="pl-9" data-testid="ygg-participant-search" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="py-2 px-2">Çalışan</th>
              <th className="px-2">Departman</th>
              <th className="px-2 text-center">Anket</th>
              <th className="px-2 text-center">Yön.</th>
              <th className="px-2 text-center">Dir.</th>
              <th className="px-2 text-center">Kal.</th>
              <th className="px-2">Durum</th>
              <th className="px-2">Yetenek Kodu</th>
              <th className="px-2">Aksiyon</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const step = nextStep(p);
              return (
                <tr key={p.employee_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`ygg-participant-row-${p.employee_id}`}>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      {p.avatar ? <img src={p.avatar} alt="" className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-slate-200" />}
                      <div>
                        <div className="font-medium leading-tight">{p.employee_name}</div>
                        <div className="text-[11px] text-slate-500 leading-tight">{p.employee_role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 text-slate-500 text-xs">{p.department || "-"}</td>
                  {STAGE_ORDER.map((s) => (
                    <td key={s} className="px-2 text-center">
                      {p.stage_status?.[s] === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" /> : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                  ))}
                  <td className="px-2">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded whitespace-nowrap ${step.cls}`}>{step.label}</span>
                  </td>
                  <td className="px-2 text-xs whitespace-nowrap">{p.talent_code ? (CODE_LABELS[p.talent_code] || p.talent_code) : "-"}</td>
                  <td className="px-2">
                    <Button size="sm" variant="outline" onClick={() => setSelected(p)} className="h-7 text-xs whitespace-nowrap" data-testid={`ygg-participant-edit-${p.employee_id}`}>
                      İncele + Kalibre
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-sm text-slate-400">Bu filtre/aramaya uyan katılımcı yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <ParticipantDrawer
          cycleId={cycle.id}
          participant={selected}
          metadata={metadata}
          onClose={() => { setSelected(null); onRefresh(); }}
          onSaved={() => { setSelected(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function Stepper({ stageStatus }) {
  return (
    <div className="flex items-center gap-1" data-testid="ygg-drawer-stepper">
      {STAGE_ORDER.map((s, i) => {
        const done = stageStatus?.[s] === "completed";
        return (
          <div key={s} className="flex items-center gap-1 flex-1">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold flex-1 justify-center ${done ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
              {done ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-slate-300 inline-block" />}
              {STAGE_LABEL[s]}
            </div>
            {i < STAGE_ORDER.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

function ParticipantDrawer({ cycleId, participant, metadata, onClose, onSaved }) {
  const [evalData, setEvalData] = useState(null);
  const [dir, setDir] = useState({});
  const [cal, setCal] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getYggEvaluation(cycleId, participant.employee_id).then((e) => {
      setEvalData(e);
      setDir(e.director_review || {});
      setCal(e.calibration || {});
    });
  }, [cycleId, participant.employee_id]);

  const saveDirector = async () => {
    setBusy(true);
    try {
      await submitYggDirectorReview(cycleId, participant.employee_id, dir);
      toast.success("Direktör onayı kaydedildi");
      setEvalData((e) => ({ ...e, stage_status: { ...(e?.stage_status || {}), director_review: "completed" } }));
    }
    catch { toast.error("Kaydedilemedi"); } finally { setBusy(false); }
  };
  const saveCalib = async () => {
    if (!cal.talent_code) { toast.error("Yetenek kodu seçin"); return; }
    setBusy(true);
    try {
      await submitYggCalibration(cycleId, participant.employee_id, cal);
      toast.success("Kalibrasyon kaydedildi"); onSaved();
    } catch { toast.error("Kaydedilemedi"); } finally { setBusy(false); }
  };

  const ctx = evalData?.employee_context;
  const survey = evalData?.survey;
  const mgr = evalData?.manager_eval;
  const history = evalData?.history || [];
  const compValues = Object.values(mgr?.competencies || {}).filter((v) => typeof v === "number");
  const compAvg = compValues.length ? (compValues.reduce((a, b) => a + b, 0) / compValues.length).toFixed(1) : null;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white max-h-[88vh] overflow-y-auto" data-testid="ygg-participant-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {ctx?.avatar && <img src={ctx.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />}
            <span>
              {participant.employee_name}
              <span className="block text-xs font-normal text-slate-500 mt-0.5">{[ctx?.role_title, ctx?.department].filter(Boolean).join(" · ")}</span>
            </span>
          </DialogTitle>
          <DialogDescription>Sürecin tüm aşamalarını gör, direktör onayı ve İK kalibrasyonunu buradan tamamla.</DialogDescription>
        </DialogHeader>

        {!evalData ? (
          <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Yükleniyor…</div>
        ) : (
          <div className="space-y-4">
            <Stepper stageStatus={evalData.stage_status} />

            {/* Employee metrics */}
            <div className="flex flex-wrap gap-2" data-testid="ygg-drawer-metrics">
              {ctx?.performance_rating != null && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-sky-50 text-sky-800 border border-sky-100"><TrendingUp className="w-3.5 h-3.5" /> Performans {ctx.performance_rating}{ctx.performance_trend ? ` ${ctx.performance_trend}` : ""}</span>}
              {ctx?.engagement_score != null && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-100"><Heart className="w-3.5 h-3.5" /> Bağlılık %{ctx.engagement_score}</span>}
              {ctx?.potential && <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-100">Potansiyel: {ctx.potential === "yuksek" ? "Yüksek" : ctx.potential === "orta" ? "Orta" : ctx.potential}</span>}
              {history.map((h) => (
                <span key={h.cycle_year} className="inline-flex items-center px-2 py-1 rounded-lg text-[11px] font-medium bg-indigo-50 text-indigo-800 border border-indigo-100">{h.cycle_year} · {CODE_LABELS[h.talent_code] || h.talent_code}{h.pys_letter ? ` · ${h.pys_letter}` : ""}</span>
              ))}
            </div>

            {/* Stage 1: Survey summary */}
            <div className="p-4 rounded-lg bg-sky-50/60 border border-sky-100 space-y-2" data-testid="ygg-drawer-survey">
              <div className="text-xs uppercase font-semibold text-sky-800 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> 1. Çalışan Anketi {evalData.stage_status?.survey === "completed" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <span className="text-[10px] font-normal text-slate-500">(henüz doldurulmadı)</span>}</div>
              {survey ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-700">
                  {(survey.top3_strengths || []).length > 0 && <div><span className="font-semibold text-emerald-700">Güçlü:</span> {(survey.top3_strengths || []).join(", ")}</div>}
                  {(survey.top3_dev_areas || []).length > 0 && <div><span className="font-semibold text-amber-700">Gelişim:</span> {(survey.top3_dev_areas || []).join(", ")}</div>}
                  {survey.vertical_target && <div><span className="font-semibold">Dikey hedef:</span> {survey.vertical_target}</div>}
                  {survey.horizontal_target && <div><span className="font-semibold">Yatay hedef:</span> {survey.horizontal_target}</div>}
                  {survey.support_needed && <div className="md:col-span-2"><span className="font-semibold">İhtiyaç duyduğu destek:</span> {survey.support_needed}</div>}
                  {survey.english_level && <div><span className="font-semibold">İngilizce:</span> {survey.english_level}</div>}
                </div>
              ) : <div className="text-xs text-slate-500 italic">Çalışan öz-değerlendirme anketini doldurduğunda cevaplar burada görünecek.</div>}
            </div>

            {/* Stage 2: Manager eval summary */}
            <div className="p-4 rounded-lg bg-indigo-50/60 border border-indigo-100 space-y-2" data-testid="ygg-drawer-mgr">
              <div className="text-xs uppercase font-semibold text-indigo-800 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> 2. Yönetici Değerlendirmesi {evalData.stage_status?.manager_eval === "completed" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <span className="text-[10px] font-normal text-slate-500">(bekleniyor)</span>}</div>
              {mgr ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-700">
                  {compAvg && <div><span className="font-semibold">Yetkinlik ortalaması:</span> {compAvg} / 5</div>}
                  {mgr.pys_letter_pre && <div><span className="font-semibold">PYS (öncesi):</span> {mgr.pys_letter_pre}</div>}
                  {mgr.mobility && <div><span className="font-semibold">Mobilite:</span> {mgr.mobility}</div>}
                  {mgr.risk_status && <div><span className="font-semibold">Risk:</span> {mgr.risk_status}</div>}
                  {mgr.strengths && <div className="md:col-span-2"><span className="font-semibold text-emerald-700">Güçlü yönler:</span> {mgr.strengths}</div>}
                  {mgr.dev_areas && <div className="md:col-span-2"><span className="font-semibold text-amber-700">Gelişim alanları:</span> {mgr.dev_areas}</div>}
                  {mgr.backs_up_who && <div><span className="font-semibold">Kimi yedekliyor:</span> {mgr.backs_up_who}</div>}
                  {mgr.backup_person && <div><span className="font-semibold">Yedeği:</span> {mgr.backup_person}</div>}
                  {mgr.notes && <div className="md:col-span-2 italic">&ldquo;{mgr.notes}&rdquo;</div>}
                </div>
              ) : <div className="text-xs text-slate-500 italic">Yönetici değerlendirmesini tamamladığında özet burada görünecek.</div>}
            </div>

            {/* Stage 3: Director */}
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
              <div className="text-xs uppercase font-semibold text-slate-600 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> 3. Direktör Onayı {evalData.stage_status?.director_review === "completed" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}</div>
              <Textarea rows={2} placeholder="Direktör notu" value={dir.director_note || ""} onChange={(e) => setDir({ ...dir, director_note: e.target.value })} data-testid="ygg-dir-note" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select value={dir.promotion_eligibility || ""} onValueChange={(v) => setDir({ ...dir, promotion_eligibility: v })}>
                  <SelectTrigger className="bg-white" data-testid="ygg-dir-eligibility"><SelectValue placeholder="Terfi Uygunluk" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="uygun">Uygun</SelectItem>
                    <SelectItem value="gözlemde">Gözlemde</SelectItem>
                    <SelectItem value="beklemede">Beklemede</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={dir.decision_reason || ""} onChange={(e) => setDir({ ...dir, decision_reason: e.target.value })} placeholder="Karar nedeni" data-testid="ygg-dir-reason" />
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={saveDirector} disabled={busy} data-testid="ygg-dir-save">{busy ? "..." : "Direktör Onayını Kaydet"}</Button>
              </div>
            </div>

            {/* Stage 4: Calibration */}
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100 space-y-3">
              <div className="text-xs uppercase font-semibold text-amber-800 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> 4. Kalibrasyon (İK Final) {evalData.stage_status?.calibration === "completed" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select value={cal.pys_letter_post || ""} onValueChange={(v) => setCal({ ...cal, pys_letter_post: v })}>
                  <SelectTrigger className="bg-white" data-testid="ygg-cal-pys"><SelectValue placeholder="Kalibre PYS" /></SelectTrigger>
                  <SelectContent className="bg-white">{["A","B","C","D"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={cal.talent_code || ""} onValueChange={(v) => setCal({ ...cal, talent_code: v })}>
                  <SelectTrigger className="bg-white" data-testid="ygg-cal-code"><SelectValue placeholder="Yetenek Kodu" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(metadata?.talent_codes || []).map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={cal.talent_horizon || ""} onValueChange={(v) => setCal({ ...cal, talent_horizon: v })}>
                  <SelectTrigger className="bg-white" data-testid="ygg-cal-horizon"><SelectValue placeholder="Vade" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="kısa vade">Kısa Vade (0-12 ay)</SelectItem>
                    <SelectItem value="orta vade">Orta Vade (12-24 ay)</SelectItem>
                    <SelectItem value="uzun vade">Uzun Vade (24+ ay)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea rows={2} placeholder="Kalibrasyon notu (öncesi/sonrası açıklama)" value={cal.calibration_note || ""} onChange={(e) => setCal({ ...cal, calibration_note: e.target.value })} data-testid="ygg-cal-note" />
              <div className="flex justify-end">
                <Button size="sm" onClick={saveCalib} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="ygg-cal-save">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : "Kalibrasyonu Kilitle"}
                </Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Kapat</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProcessExplainer() {
  const steps = [
    { n: 1, icon: User,        title: "Anket",         who: "Çalışan",  desc: "Öz-değerlendirme + kariyer hedefleri" },
    { n: 2, icon: Users,       title: "Değerlendirme", who: "Yönetici", desc: "10 yetkinlik + risk + yedekleme + PYS" },
    { n: 3, icon: ShieldCheck, title: "Onay",          who: "Direktör", desc: "Terfi uygunluk + karar nedeni" },
    { n: 4, icon: Award,       title: "Kalibrasyon",   who: "İK",       desc: "Post-PYS + yetenek kodu + vade" },
  ];
  return (
    <div className="rounded-xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-emerald-50 p-4" data-testid="ygg-process-explainer">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="w-4 h-4 text-sky-700" />
        <div className="text-sm font-semibold text-sky-900">YGG Süreci Nasıl İşliyor?</div>
        <div className="text-xs text-slate-500 ml-2">İK döngüyü başlatır → her katılımcı için 4 aşama sırayla ilerler</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.n} className="relative bg-white rounded-lg p-3 border border-slate-100">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-6 h-6 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center">{s.n}</span>
                <Icon className="w-3.5 h-3.5 text-sky-700" />
                <span className="text-sm font-semibold text-slate-800">{s.title}</span>
              </div>
              <div className="text-[11px] font-semibold text-emerald-700">{s.who}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>
              {i < steps.length - 1 && (
                <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 z-10" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewCycleDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [d1, setD1] = useState(""); const [d2, setD2] = useState(""); const [d3, setD3] = useState(""); const [d4, setD4] = useState("");
  const [busy, setBusy] = useState(false);
  // Participant selection
  const [allEmployees, setAllEmployees] = useState([]);
  const [scope, setScope] = useState("all"); // all | department | manual
  const [dept, setDept] = useState("");
  const [manualIds, setManualIds] = useState(new Set());
  const [manualQuery, setManualQuery] = useState("");

  useEffect(() => {
    if (open) fetchEmployees().then((d) => setAllEmployees(Array.isArray(d) ? d : (d.employees || [])));
  }, [open]);

  const departments = Array.from(new Set(allEmployees.map((e) => e.department).filter(Boolean))).sort();
  const filteredForManual = allEmployees.filter((e) => !manualQuery.trim() || (e.name || "").toLowerCase().includes(manualQuery.trim().toLowerCase()));

  const toggleManual = (id) => {
    const next = new Set(manualIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setManualIds(next);
  };

  const computeParticipants = () => {
    if (scope === "all") return null; // backend auto-fills
    if (scope === "department" && dept) return allEmployees.filter((e) => e.department === dept).map((e) => e.id);
    if (scope === "manual") return Array.from(manualIds);
    return null;
  };

  const save = async () => {
    if (!name) { toast.error("Ad girin"); return; }
    const participants = computeParticipants();
    if (scope !== "all" && (!participants || participants.length === 0)) {
      toast.error("En az bir katılımcı seçin");
      return;
    }
    setBusy(true);
    try {
      await createYggCycle({
        name, year: parseInt(year),
        deadline_survey: d1 || null, deadline_manager_eval: d2 || null,
        deadline_director_review: d3 || null, deadline_calibration: d4 || null,
        participant_ids: participants,
      });
      toast.success(`YGG döngüsü oluşturuldu (${participants ? participants.length : "tüm çalışanlar"})`);
      onOpenChange(false); onSaved();
      setName(""); setD1(""); setD2(""); setD3(""); setD4(""); setScope("all"); setDept(""); setManualIds(new Set()); setManualQuery("");
    } catch { toast.error("Oluşturulamadı"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="ygg-new-cycle-dialog">
        <DialogHeader>
          <DialogTitle>Yeni YGG Döngüsü</DialogTitle>
          <DialogDescription>Adım 1: Temel bilgi · Adım 2: Kimler dahil? · Adım 3: Son tarihler</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Step 1: Basic */}
          <div className="p-3 rounded-lg bg-slate-50 border border-slate-100 space-y-2">
            <div className="text-xs uppercase font-semibold text-slate-600">1. Temel Bilgi</div>
            <Input placeholder="Örn: 2026 Yetenek Gözden Geçirme" value={name} onChange={(e) => setName(e.target.value)} data-testid="ygg-new-name" />
            <Input placeholder="Yıl" value={year} onChange={(e) => setYear(e.target.value)} type="number" data-testid="ygg-new-year" />
          </div>

          {/* Step 2: Participants */}
          <div className="p-3 rounded-lg bg-sky-50 border border-sky-100 space-y-2">
            <div className="text-xs uppercase font-semibold text-sky-800">2. Katılımcılar</div>
            <div className="flex flex-wrap gap-2 text-sm">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="scope" value="all" checked={scope === "all"} onChange={() => setScope("all")} data-testid="ygg-scope-all" /> Tüm çalışanlar ({allEmployees.length})
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="scope" value="department" checked={scope === "department"} onChange={() => setScope("department")} data-testid="ygg-scope-dept" /> Departmana göre
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="scope" value="manual" checked={scope === "manual"} onChange={() => setScope("manual")} data-testid="ygg-scope-manual" /> Elle seç
              </label>
            </div>
            {scope === "department" && (
              <Select value={dept} onValueChange={setDept}>
                <SelectTrigger className="bg-white" data-testid="ygg-scope-dept-select"><SelectValue placeholder="Departman seç..." /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{departments.map((d) => <SelectItem key={d} value={d}>{d} ({allEmployees.filter((e) => e.department === d).length})</SelectItem>)}</SelectContent>
              </Select>
            )}
            {scope === "manual" && (
              <div className="space-y-1.5">
                <Input value={manualQuery} onChange={(e) => setManualQuery(e.target.value)} placeholder="İsimle ara…" className="bg-white" data-testid="ygg-scope-manual-search" />
                <div className="max-h-52 overflow-y-auto border border-sky-200 rounded-md bg-white p-2" data-testid="ygg-scope-manual-list">
                  <div className="text-[11px] text-sky-800 mb-1.5">Seçili: {manualIds.size}</div>
                  {filteredForManual.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-sky-50 rounded px-1">
                      <input type="checkbox" checked={manualIds.has(e.id)} onChange={() => toggleManual(e.id)} data-testid={`ygg-scope-emp-${e.id}`} />
                      <span className="flex-1">{e.name}</span>
                      <span className="text-[11px] text-slate-500">{e.role_title}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Deadlines */}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 space-y-2">
            <div className="text-xs uppercase font-semibold text-amber-800">3. Aşama Son Tarihleri (opsiyonel)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500">Anket son tarih</label>
                <Input type="date" value={d1} onChange={(e) => setD1(e.target.value)} data-testid="ygg-new-d1" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Yönetici son tarih</label>
                <Input type="date" value={d2} onChange={(e) => setD2(e.target.value)} data-testid="ygg-new-d2" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Direktör son tarih</label>
                <Input type="date" value={d3} onChange={(e) => setD3(e.target.value)} data-testid="ygg-new-d3" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Kalibrasyon son tarih</label>
                <Input type="date" value={d4} onChange={(e) => setD4(e.target.value)} data-testid="ygg-new-d4" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
          <Button onClick={save} disabled={busy} data-testid="ygg-new-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Oluştur"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
